/**
 * Graph Helper Utilities
 * Layout, sizing, and transformation helpers for graph visualization
 */

/**
 * Returns a uniform node size for all organization nodes.
 * Uniform sizing avoids implying that well-connected orgs are more important.
 */
export function getNodeSize() {
    return 6;
}

/**
 * Transform API response to react-force-graph format
 * @param {object} graphData - API response with nodes and edges
 * @param {object} connectionCounts - Map of node id to connection count
 * @returns {object} - Formatted graph data for react-force-graph
 */
export function transformGraphData(graphData, connectionCounts = {}) {
    if (!graphData || !graphData.nodes) {
        return { nodes: [], links: [] };
    }

    const nodes = graphData.nodes.map((node) => ({
        id: node.id,
        name: node.name,
        primarySector: node.primarySector,
        regionsServed: node.regionsServed || [],
        programmingTypes: node.programmingTypes || [],
        type: node.type,
        // Add connection count for sizing
        connectionCount: connectionCounts[node.id] || 0,
    }));

    // Transform edges to links (react-force-graph uses 'links' terminology)
    const links = (graphData.edges || []).map((edge) => ({
        source: edge.source,
        target: edge.target,
        type: edge.type,
    }));

    return { nodes, links };
}

/**
 * Calculate connection counts for each node from edges
 * @param {Array} edges - Array of edge objects with source and target
 * @returns {object} - Map of node id to connection count
 */
export function calculateConnectionCounts(edges = []) {
    const counts = {};
    edges.forEach((edge) => {
        counts[edge.source] = (counts[edge.source] || 0) + 1;
        counts[edge.target] = (counts[edge.target] || 0) + 1;
    });
    return counts;
}

/**
 * Filter graph data by search term
 * @param {object} graphData - Graph data with nodes and links
 * @param {string} searchTerm - Search query
 * @returns {object} - Filtered graph data
 */
export function filterGraphBySearch(graphData, searchTerm) {
    // Require at least 2 characters to filter (matches autocomplete behavior)
    if (!searchTerm || searchTerm.length < 2 || !graphData.nodes) {
        return graphData;
    }

    const term = searchTerm.toLowerCase();
    const matchingIds = new Set();

    // Find nodes whose names match the search term
    graphData.nodes.forEach((node) => {
        if (node.name.toLowerCase().includes(term)) {
            matchingIds.add(node.id);
        }
    });

    // Only show matching nodes (strict filter for search-as-you-type)
    const filteredNodes = graphData.nodes.filter((node) =>
        matchingIds.has(node.id)
    );

    // Only include links between matching nodes
    const filteredLinks = graphData.links.filter((link) => {
        const sourceId =
            typeof link.source === 'object' ? link.source.id : link.source;
        const targetId =
            typeof link.target === 'object' ? link.target.id : link.target;
        return matchingIds.has(sourceId) && matchingIds.has(targetId);
    });

    return { nodes: filteredNodes, links: filteredLinks };
}

/**
 * Highlight nodes matching search term
 * @param {Array} nodes - Array of node objects
 * @param {string} searchTerm - Search query
 * @returns {Set} - Set of node IDs that match
 */
export function getHighlightedNodes(nodes = [], searchTerm = '') {
    // Require at least 2 characters to highlight (matches autocomplete behavior)
    if (!searchTerm || searchTerm.length < 2) {
        return new Set();
    }

    const term = searchTerm.toLowerCase();
    const highlighted = new Set();

    nodes.forEach((node) => {
        if (node.name.toLowerCase().includes(term)) {
            highlighted.add(node.id);
        }
    });

    return highlighted;
}

/**
 * Filter graph data by minimum connection count
 * @param {object} graphData - Graph data with nodes and links
 * @param {number} minConnections - Minimum number of connections required
 * @returns {object} - Filtered graph data
 */
export function filterByMinConnections(graphData, minConnections) {
    if (!minConnections || !graphData.nodes) {
        return graphData;
    }

    const minCount = parseInt(minConnections, 10);
    if (isNaN(minCount) || minCount <= 0) {
        return graphData;
    }

    // Filter nodes to only include those with enough connections
    const matchingIds = new Set();
    graphData.nodes.forEach((node) => {
        if ((node.connectionCount || 0) >= minCount) {
            matchingIds.add(node.id);
        }
    });

    const filteredNodes = graphData.nodes.filter((node) =>
        matchingIds.has(node.id)
    );

    // Only include links between matching nodes
    const filteredLinks = graphData.links.filter((link) => {
        const sourceId =
            typeof link.source === 'object' ? link.source.id : link.source;
        const targetId =
            typeof link.target === 'object' ? link.target.id : link.target;
        return matchingIds.has(sourceId) && matchingIds.has(targetId);
    });

    return { nodes: filteredNodes, links: filteredLinks };
}

/**
 * Filter out disconnected (isolated) nodes from graph data
 * Useful for preview graphs where disconnected nodes create visual clutter
 * @param {object} graphData - Graph data with nodes and links
 * @returns {object} - Graph data with only connected nodes
 */
export function filterConnectedNodes(graphData) {
    if (!graphData || !graphData.nodes || !graphData.links) {
        return { nodes: [], links: [] };
    }

    // Build set of node IDs that have at least one connection
    const connectedIds = new Set();
    graphData.links.forEach((link) => {
        const sourceId =
            typeof link.source === 'object' ? link.source.id : link.source;
        const targetId =
            typeof link.target === 'object' ? link.target.id : link.target;
        connectedIds.add(sourceId);
        connectedIds.add(targetId);
    });

    // Filter nodes to only include connected ones
    const filteredNodes = graphData.nodes.filter((node) =>
        connectedIds.has(node.id)
    );

    return { nodes: filteredNodes, links: graphData.links };
}

/**
 * Get node tooltip content
 * @param {object} node - Node object
 * @returns {string} - HTML string for tooltip
 */
export function getNodeTooltip(node) {
    const regions =
        node.regionsServed && node.regionsServed.length > 0
            ? node.regionsServed.join(', ')
            : 'N/A';

    return `
        <div style="padding: 8px; max-width: 250px;">
            <strong style="font-size: 14px;">${node.name}</strong>
            <div style="margin-top: 4px; font-size: 12px; color: #666;">
                <div><strong>Sector:</strong> ${node.primarySector || 'N/A'}</div>
                <div><strong>Regions:</strong> ${regions}</div>
                ${node.connectionCount ? `<div><strong>Connections:</strong> ${node.connectionCount}</div>` : ''}
            </div>
        </div>
    `;
}
